import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import SetDefaultRecycledValuesButton from "../components/button/SetDefaultValuesButton";
import AgGrid from "../components/grid/AgGrid";
import { defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { Circularity } from "../db_access/circularity";
import { calculate_uf_calculations, recalculate_row_mci } from "../functions/CircularityFunctions";
import ColumnMenu from "../components/context-menu/ColumnMenu";

function UtilityFactorTable({ circularities, service_life, columnMenuVisible, setColumnMenuVisible, anchorRef, setCircularities, topic = "Product" }) {
  const { isSaveClicked, setIsSaveVisible, setRefresh, setIsLoading, setIsSaveClicked, columnVisibilities } = useContextProvider();
  const [utilities, setUtilities] = useState([]);
  const [modified, setModified] = useState(false);
  const gridRef = useRef(null);

  const colVisibility_key = "circularity_utility_factor_table_visibility";

  const rawColDefs = [
    {
      headerName: "SI No.",
      valueGetter: "node.rowIndex + 1",
      width: 80,
      minWidth: 60,
      headerClass: "ag-center-aligned-header",
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      suppressMenu: true,
      sortable: false,
      field: "si_no"
    },
    { field: "description", headerName: "Description", ...leftAlignColDef, ...defaultColDef, flex: 1 },
    { field: "uf_product_life", headerName: `${topic} life (Years)`, ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    { field: "uf_industry_avg_life", headerName: "Industry average (Years)", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    { field: "uf_functional_units", headerName: "Functional units over life time", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    { field: "uf_industry_avg_units", headerName: "Industry average", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    { field: "uf_calculations", headerName: "Utility factor", ...rightAlignColDef, ...defaultColDef, ...floatFormater },
  ];

  const defaultCols = useMemo(() => rawColDefs.map((c) => c.field), []);

  const columnDefs = useMemo(() => {
    const isHidden = (field) => {
      if (field === "si_no") return false;
      return !columnVisibilities[colVisibility_key]?.includes(field);
    };
    return rawColDefs.map((col) => ({
      ...col,
      hide: isHidden(col.field),
    }));
  }, [columnVisibilities, colVisibility_key, topic]);

  useEffect(() => {
    if (modified || isSaveClicked) return;
    const updated = circularities.map(uf => {
      const new_uf = { ...uf };
      if (!new_uf.uf_product_life) new_uf.uf_product_life = service_life;
      if (!new_uf.uf_industry_avg_life) new_uf.uf_industry_avg_life = service_life;
      // Recalculate if we applied defaults
      if (new_uf.uf_product_life !== uf.uf_product_life || new_uf.uf_industry_avg_life !== uf.uf_industry_avg_life) {
        new_uf.uf_calculations = calculate_uf_calculations(new_uf);
        // Removed setModified(true) to prevent Save button loop on load
      }
      return new_uf;
    });
    setUtilities(updated);
  }, [circularities, service_life, modified, isSaveClicked]);




  const setDefaultRecycledValues = useCallback(() => {
    const updatedData = utilities.map((uf) => {
      const new_uf = { ...uf };
      new_uf.uf_product_life = service_life;
      new_uf.uf_industry_avg_life = service_life;
      return recalculate_row_mci(new_uf);
    });
    setUtilities(updatedData);
    setModified(true);
    if (setCircularities) {
      setCircularities(updatedData);
    }
  }, [utilities, service_life, setCircularities]);


  const onCellValueChanged = (params) => {
    const { data, colDef, value } = params;
    const updated = utilities.map((uf) => {
      if (uf.id === data.id) {
        const new_uf = { ...uf, [colDef.field]: value };
        return recalculate_row_mci(new_uf);
      } else {
        return uf;
      }
    });
    setUtilities(updated);
    setModified(true);
    // Notify parent for live summary updates
    if (setCircularities) {
      setCircularities(updated);
    }
  };


  useEffect(() => {
    if (isSaveClicked && modified) {
      const payload = utilities
        .filter(item => item.id && Number(item.id) !== 0)
        .map((item) => {
          return {
            id: Number(item.id),
            description: item.description,
            uf_product_life: Number(item.uf_product_life || 0),
            uf_industry_avg_life: Number(item.uf_industry_avg_life || 0),
            uf_functional_units: Number(item.uf_functional_units || 0),
            uf_industry_avg_units: Number(item.uf_industry_avg_units || 0)
          };
        });
      
      if (payload.length > 0) {
        Circularity.update_utilities(payload).finally(() => {
          setModified(false);
          // Parent handles setIsLoading and setIsSaveClicked after the 2s MCI sync
        });

      }
    }
  }, [isSaveClicked]);


  useEffect(() => {
    if (modified) {
      setIsSaveVisible(true);
    }
  }, [modified]);

  return (
    <Box sx={{ height: "auto" }}>
      <AgGrid 
        ref={gridRef}
        columnDefs={columnDefs} 
        rowData={utilities} 
        onCellValueChanged={onCellValueChanged} 
        headerCheckbox={false} 
      />
      <ColumnMenu
        anchorRef={anchorRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={rawColDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
        gridRef={gridRef}
      />
    </Box>
  );
}

export default UtilityFactorTable;

